/**
 * Client for viveka-ftw's own privileged-transition server endpoints
 * (server/api/checkout/*). Bodies carry Money/UUID/Date values, which
 * plain JSON can't round-trip — both directions use Sharetribe's transit
 * encoding instead, mirroring server/api-util/sdk.js on the other end.
 *
 * Only used for the checkout transitions that process.edn marks
 * `:privileged? true` — every other API call in this app still goes
 * straight to Flex via the browser SDK.
 */
import axios from 'axios';
import Decimal from 'decimal.js';
import { types as sdkTypes, transit } from './sdkLoader';

// Keep in sync with typeHandlers in server/api-util/sdk.js — both sides
// must agree on how Money/BigDecimal round-trips through transit.
export const typeHandlers = [
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => transit.write(data, { typeHandlers });
const deserialize = str => transit.read(str, { typeHandlers });

const post = (path, body) => {
  return axios
    .post(path, serialize(body), {
      headers: { 'Content-Type': 'application/transit+json' },
      transformResponse: [data => data], // keep the raw transit string — no auto JSON.parse
    })
    .then(res => deserialize(res.data))
    .catch(err => {
      const data = err.response && err.response.data;
      const deserialized = data ? (() => {
        try { return deserialize(data); } catch (e) { return null; }
      })() : null;
      throw deserialized || err;
    });
};

// See server/api/checkout/initiatePrivileged.js for the expected body shape.
export const initiatePrivileged = body => post('/api/checkout/initiate-privileged', body);

// See server/api/checkout/transitionPrivileged.js for the expected body shape.
export const transitionPrivileged = body => post('/api/checkout/transition-privileged', body);
